import FunnelsAPI from '../../api/funnels';
import ContactAPI from '../../api/contacts';

// Local mutation types keep this module self-contained (no core mutation-types touch).
const SET_FUNNEL_UI_FLAG = 'SET_FUNNEL_UI_FLAG';
const SET_FUNNELS = 'SET_FUNNELS';
const SET_FUNNEL_CARDS = 'SET_FUNNEL_CARDS';
const SET_FUNNEL_STAGES = 'SET_FUNNEL_STAGES';
const UPDATE_FUNNEL_CARD = 'UPDATE_FUNNEL_CARD';
const REMOVE_FUNNEL_CARD = 'REMOVE_FUNNEL_CARD';
const SET_ARCHIVED_FUNNEL_CARDS = 'SET_ARCHIVED_FUNNEL_CARDS';
const REMOVE_ARCHIVED_FUNNEL_CARD = 'REMOVE_ARCHIVED_FUNNEL_CARD';
const SET_CONTACT_FUNNEL_CARDS = 'SET_CONTACT_FUNNEL_CARDS';
const ADD_FUNNEL = 'ADD_FUNNEL';
const UPDATE_FUNNEL = 'UPDATE_FUNNEL';
const DELETE_FUNNEL = 'DELETE_FUNNEL';
const ADD_FUNNEL_STAGE = 'ADD_FUNNEL_STAGE';
const UPDATE_FUNNEL_STAGE = 'UPDATE_FUNNEL_STAGE';
const DELETE_FUNNEL_STAGE = 'DELETE_FUNNEL_STAGE';
const SET_FUNNEL_FIELDS = 'SET_FUNNEL_FIELDS';
const ADD_FUNNEL_FIELD = 'ADD_FUNNEL_FIELD';
const UPDATE_FUNNEL_FIELD = 'UPDATE_FUNNEL_FIELD';
const DELETE_FUNNEL_FIELD = 'DELETE_FUNNEL_FIELD';

const POSITION_GAP = 1000;

// Returns cardsByFunnel without the given card, leaving other funnels untouched.
const withoutCard = (cardsByFunnel, card) => ({
  ...cardsByFunnel,
  [card.funnel_id]: (cardsByFunnel[card.funnel_id] || []).filter(
    item => item.id !== card.id
  ),
});

const state = {
  funnels: [],
  cardsByFunnel: {},
  // Kept in its own slot rather than reusing cardsByFunnel: the two lists are
  // disjoint by definition, and sharing the slot would make the archived fetch
  // wipe the board — and make every incoming card event ambiguous about which
  // list it belongs to.
  archivedCardsByFunnel: {},
  // The contact's cards across every funnel — read by the conversation sidebar,
  // which shows the contact's whole journey, not just one funnel's board.
  cardsByContact: {},
  uiFlags: {
    isFetchingFunnels: false,
    isFetchingCards: false,
    isFetchingArchivedCards: false,
    isFetchingContactCards: false,
  },
};

export const getters = {
  getFunnels: _state => _state.funnels,
  getFunnel: _state => id =>
    _state.funnels.find(funnel => funnel.id === Number(id)),
  getCards: _state => funnelId => _state.cardsByFunnel[funnelId] || [],
  getArchivedCards: _state => funnelId =>
    _state.archivedCardsByFunnel[funnelId] || [],
  getCardsForContact: _state => contactId =>
    _state.cardsByContact[contactId] || [],
  getUIFlags: _state => _state.uiFlags,
};

export const actions = {
  fetchFunnels: async ({ commit }) => {
    commit(SET_FUNNEL_UI_FLAG, { isFetchingFunnels: true });
    try {
      const { data } = await FunnelsAPI.get();
      commit(SET_FUNNELS, data);
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(SET_FUNNEL_UI_FLAG, { isFetchingFunnels: false });
    }
  },
  fetchCards: async ({ commit }, { funnelId }) => {
    commit(SET_FUNNEL_UI_FLAG, { isFetchingCards: true });
    try {
      const { data } = await FunnelsAPI.getCards(funnelId);
      commit(SET_FUNNEL_CARDS, { funnelId, cards: data });
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(SET_FUNNEL_UI_FLAG, { isFetchingCards: false });
    }
  },
  // Reads the contact's cards across every funnel — used by the conversation
  // sidebar, which shows the contact's whole journey, not just one funnel's board.
  fetchContactCards: async ({ commit }, { contactId }) => {
    commit(SET_FUNNEL_UI_FLAG, { isFetchingContactCards: true });
    try {
      const { data } = await ContactAPI.getFunnelCards(contactId);
      commit(SET_CONTACT_FUNNEL_CARDS, { contactId, cards: data });
    } finally {
      commit(SET_FUNNEL_UI_FLAG, { isFetchingContactCards: false });
    }
  },
  // Loads the archived cards of a funnel (?archived=true). Kept separate from
  // fetchCards so switching views never overwrites the active board.
  fetchArchivedCards: async ({ commit }, { funnelId }) => {
    commit(SET_FUNNEL_UI_FLAG, { isFetchingArchivedCards: true });
    try {
      const { data } = await FunnelsAPI.getCards(funnelId, { archived: true });
      commit(SET_ARCHIVED_FUNNEL_CARDS, { funnelId, cards: data });
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(SET_FUNNEL_UI_FLAG, { isFetchingArchivedCards: false });
    }
  },
  // Puts the card back on the board. The response carries archived_at: null, so
  // the same payload both leaves the archived list and re-enters the active one
  // through the usual upsert.
  unarchiveCard: async ({ commit }, { funnelId, cardId }) => {
    const { data } = await FunnelsAPI.unarchiveCard(funnelId, cardId);
    commit(REMOVE_ARCHIVED_FUNNEL_CARD, data);
    commit(UPDATE_FUNNEL_CARD, data);
    return data;
  },
  // Creates a card from an existing conversation. The board updates live for
  // everyone watching the funnel through the ActionCable broadcast; here we also
  // apply the returned card locally (via the same UPDATE_FUNNEL_CARD path) so the
  // creator's board reflects it immediately when the funnel is already loaded.
  createCard: async (
    { commit },
    { funnelId, funnelStageId, conversationId, customFieldValues }
  ) => {
    const { data } = await FunnelsAPI.createCard(funnelId, {
      funnelStageId,
      conversationId,
      customFieldValues,
    });
    commit(UPDATE_FUNNEL_CARD, data);
    return data;
  },
  // Persists the card's custom field values. The axios error is intentionally
  // left un-wrapped so callers can read the structured 422 (error.response) and
  // surface per-field messages.
  updateCardValues: async (
    { commit },
    { funnelId, cardId, customFieldValues }
  ) => {
    const { data } = await FunnelsAPI.updateCard(funnelId, cardId, {
      customFieldValues,
    });
    commit(UPDATE_FUNNEL_CARD, data);
    return data;
  },
  // Optimistically move the card to the destination stage, then reconcile with
  // the server response. On failure the previous snapshot is restored.
  moveCard: async (
    { commit, state: $state },
    { funnelId, cardId, funnelStageId, position }
  ) => {
    const snapshot = ($state.cardsByFunnel[funnelId] || []).map(card => ({
      ...card,
    }));
    const optimistic = snapshot.map(card =>
      card.id === cardId ? { ...card, funnel_stage_id: funnelStageId } : card
    );
    commit(SET_FUNNEL_CARDS, { funnelId, cards: optimistic });
    try {
      const { data } = await FunnelsAPI.moveCard(funnelId, cardId, {
        funnelStageId,
        position,
      });
      const reconciled = optimistic.map(card =>
        card.id === cardId ? data : card
      );
      commit(SET_FUNNEL_CARDS, { funnelId, cards: reconciled });
      return data;
    } catch (error) {
      commit(SET_FUNNEL_CARDS, { funnelId, cards: snapshot });
      // Re-throw the raw axios error (not a wrapped Error) so the board can read
      // the structured 422 (error.response.data.errors) and name the missing field.
      throw error;
    }
  },
  // Optimistically reorder the funnel stages, then reconcile with the server.
  reorderStages: async (
    { commit, getters: $getters },
    { funnelId, stageIds }
  ) => {
    const snapshot = ($getters.getFunnel(funnelId)?.stages || []).map(
      stage => ({
        ...stage,
      })
    );
    const optimistic = stageIds.map((id, index) => ({
      ...snapshot.find(stage => stage.id === id),
      position: (index + 1) * POSITION_GAP,
    }));
    commit(SET_FUNNEL_STAGES, { funnelId, stages: optimistic });
    try {
      const { data } = await FunnelsAPI.reorderStages(funnelId, stageIds);
      commit(SET_FUNNEL_STAGES, { funnelId, stages: data });
      return data;
    } catch (error) {
      commit(SET_FUNNEL_STAGES, { funnelId, stages: snapshot });
      throw new Error(error);
    }
  },
  // Applies a card received over ActionCable (e.g. another user moved it).
  // Also serves funnel.card.created and funnel.card.unarchived: the mutation
  // upserts, so a card that is not in the local list is appended to the board.
  updateCardFromEvent: ({ commit }, card) => {
    commit(UPDATE_FUNNEL_CARD, card);
  },
  // Applies funnel.card.archived: the card leaves every watching board.
  removeCardFromEvent: ({ commit }, card) => {
    commit(REMOVE_FUNNEL_CARD, card);
  },
  // Archives the card. The broadcast takes it off the other operators' boards;
  // committing the response here removes it from ours without waiting for the
  // round trip through ActionCable.
  archiveCard: async ({ commit }, { funnelId, cardId }) => {
    const { data } = await FunnelsAPI.archiveCard(funnelId, cardId);
    commit(REMOVE_FUNNEL_CARD, data);
    return data;
  },
  // --- Admin CRUD (Fatia 7) ---
  createFunnel: async ({ commit }, { name, description, teamIds }) => {
    const { data } = await FunnelsAPI.createFunnel({
      name,
      description,
      team_ids: teamIds,
    });
    commit(ADD_FUNNEL, data);
    return data;
  },
  updateFunnel: async ({ commit }, { id, name, description, teamIds }) => {
    const payload = {};
    if (name !== undefined) payload.name = name;
    if (description !== undefined) payload.description = description;
    if (teamIds !== undefined) payload.team_ids = teamIds;
    const { data } = await FunnelsAPI.updateFunnel(id, payload);
    commit(UPDATE_FUNNEL, data);
    return data;
  },
  deleteFunnel: async ({ commit }, id) => {
    await FunnelsAPI.deleteFunnel(id);
    commit(DELETE_FUNNEL, id);
  },
  createStage: async ({ commit }, { funnelId, name, color }) => {
    const { data } = await FunnelsAPI.createStage(funnelId, { name, color });
    commit(ADD_FUNNEL_STAGE, { funnelId, stage: data });
    return data;
  },
  updateStage: async ({ commit }, { funnelId, stageId, name, color }) => {
    const { data } = await FunnelsAPI.updateStage(funnelId, stageId, {
      name,
      color,
    });
    commit(UPDATE_FUNNEL_STAGE, { funnelId, stage: data });
    return data;
  },
  deleteStage: async ({ commit }, { funnelId, stageId }) => {
    await FunnelsAPI.deleteStage(funnelId, stageId);
    commit(DELETE_FUNNEL_STAGE, { funnelId, stageId });
  },
  // --- Custom fields admin (Fatia 9b) ---
  createField: async ({ commit }, { funnelId, ...payload }) => {
    const { data } = await FunnelsAPI.createField(funnelId, payload);
    commit(ADD_FUNNEL_FIELD, { funnelId, field: data });
    return data;
  },
  updateField: async ({ commit }, { funnelId, fieldId, ...payload }) => {
    const { data } = await FunnelsAPI.updateField(funnelId, fieldId, payload);
    commit(UPDATE_FUNNEL_FIELD, { funnelId, field: data });
    return data;
  },
  deleteField: async ({ commit }, { funnelId, fieldId }) => {
    await FunnelsAPI.deleteField(funnelId, fieldId);
    commit(DELETE_FUNNEL_FIELD, { funnelId, fieldId });
  },
  // Optimistically reorder the funnel fields, then reconcile with the server.
  reorderFields: async (
    { commit, getters: $getters },
    { funnelId, fieldIds }
  ) => {
    const snapshot = ($getters.getFunnel(funnelId)?.fields || []).map(
      field => ({ ...field })
    );
    const optimistic = fieldIds.map((id, index) => ({
      ...snapshot.find(field => field.id === id),
      position: (index + 1) * POSITION_GAP,
    }));
    commit(SET_FUNNEL_FIELDS, { funnelId, fields: optimistic });
    try {
      const { data } = await FunnelsAPI.reorderFields(funnelId, fieldIds);
      commit(SET_FUNNEL_FIELDS, { funnelId, fields: data });
      return data;
    } catch (error) {
      commit(SET_FUNNEL_FIELDS, { funnelId, fields: snapshot });
      throw new Error(error);
    }
  },
};

export const mutations = {
  [SET_FUNNEL_UI_FLAG]($state, data) {
    $state.uiFlags = { ...$state.uiFlags, ...data };
  },
  [SET_FUNNELS]($state, data) {
    $state.funnels = data;
  },
  [SET_FUNNEL_CARDS]($state, { funnelId, cards }) {
    $state.cardsByFunnel = { ...$state.cardsByFunnel, [funnelId]: cards };
  },
  [SET_ARCHIVED_FUNNEL_CARDS]($state, { funnelId, cards }) {
    $state.archivedCardsByFunnel = {
      ...$state.archivedCardsByFunnel,
      [funnelId]: cards,
    };
  },
  [REMOVE_ARCHIVED_FUNNEL_CARD]($state, card) {
    if (!$state.archivedCardsByFunnel[card.funnel_id]) return;

    $state.archivedCardsByFunnel = withoutCard(
      $state.archivedCardsByFunnel,
      card
    );
  },
  [SET_CONTACT_FUNNEL_CARDS]($state, { contactId, cards }) {
    $state.cardsByContact = { ...$state.cardsByContact, [contactId]: cards };
  },
  [SET_FUNNEL_STAGES]($state, { funnelId, stages }) {
    $state.funnels = $state.funnels.map(funnel =>
      funnel.id === Number(funnelId) ? { ...funnel, stages } : funnel
    );
  },
  [ADD_FUNNEL]($state, funnel) {
    $state.funnels = [...$state.funnels, funnel];
  },
  [UPDATE_FUNNEL]($state, funnel) {
    $state.funnels = $state.funnels.map(item =>
      item.id === funnel.id ? funnel : item
    );
  },
  [DELETE_FUNNEL]($state, id) {
    $state.funnels = $state.funnels.filter(item => item.id !== id);
  },
  [ADD_FUNNEL_STAGE]($state, { funnelId, stage }) {
    $state.funnels = $state.funnels.map(funnel =>
      funnel.id === Number(funnelId)
        ? { ...funnel, stages: [...(funnel.stages || []), stage] }
        : funnel
    );
  },
  [UPDATE_FUNNEL_STAGE]($state, { funnelId, stage }) {
    $state.funnels = $state.funnels.map(funnel =>
      funnel.id === Number(funnelId)
        ? {
            ...funnel,
            stages: (funnel.stages || []).map(item =>
              item.id === stage.id ? stage : item
            ),
          }
        : funnel
    );
  },
  [DELETE_FUNNEL_STAGE]($state, { funnelId, stageId }) {
    $state.funnels = $state.funnels.map(funnel =>
      funnel.id === Number(funnelId)
        ? {
            ...funnel,
            stages: (funnel.stages || []).filter(item => item.id !== stageId),
          }
        : funnel
    );
  },
  [SET_FUNNEL_FIELDS]($state, { funnelId, fields }) {
    $state.funnels = $state.funnels.map(funnel =>
      funnel.id === Number(funnelId) ? { ...funnel, fields } : funnel
    );
  },
  [ADD_FUNNEL_FIELD]($state, { funnelId, field }) {
    $state.funnels = $state.funnels.map(funnel =>
      funnel.id === Number(funnelId)
        ? { ...funnel, fields: [...(funnel.fields || []), field] }
        : funnel
    );
  },
  [UPDATE_FUNNEL_FIELD]($state, { funnelId, field }) {
    $state.funnels = $state.funnels.map(funnel =>
      funnel.id === Number(funnelId)
        ? {
            ...funnel,
            fields: (funnel.fields || []).map(item =>
              item.id === field.id ? field : item
            ),
          }
        : funnel
    );
  },
  [DELETE_FUNNEL_FIELD]($state, { funnelId, fieldId }) {
    $state.funnels = $state.funnels.map(funnel =>
      funnel.id === Number(funnelId)
        ? {
            ...funnel,
            fields: (funnel.fields || []).filter(item => item.id !== fieldId),
          }
        : funnel
    );
  },
  [UPDATE_FUNNEL_CARD]($state, card) {
    const existing = $state.cardsByFunnel[card.funnel_id];
    // Funnel not loaded in this browser: nothing to update.
    if (!existing) return;

    // An archived card does not belong on the board. Guarding here rather than
    // only in the event handler means no path can resurrect it — not a `move`
    // or `update` broadcast for an archived card, not an HTTP response applied
    // locally. This mutation is the single door into cardsByFunnel.
    if (card.archived_at) {
      $state.cardsByFunnel = withoutCard($state.cardsByFunnel, card);
      return;
    }

    const current = existing.find(item => item.id === card.id);
    // Idempotency: skip when the local card already matches the event (e.g. the
    // move that generated this event was our own optimistic update). Custom
    // field values are part of the signature so a values-only edit still applies
    // (stage/position unchanged) on every watching board.
    if (
      current &&
      current.funnel_stage_id === card.funnel_stage_id &&
      current.position === card.position &&
      JSON.stringify(current.custom_field_values || {}) ===
        JSON.stringify(card.custom_field_values || {})
    ) {
      return;
    }

    const cards = current
      ? existing.map(item =>
          item.id === card.id ? { ...item, ...card } : item
        )
      : [...existing, card];
    $state.cardsByFunnel = { ...$state.cardsByFunnel, [card.funnel_id]: cards };
  },
  // Takes the card off the board. UPDATE_FUNNEL_CARD only ever upserts, so
  // archiving needs its own mutation — reusing the update path would re-add the
  // very card the operator just archived.
  [REMOVE_FUNNEL_CARD]($state, card) {
    if (!$state.cardsByFunnel[card.funnel_id]) return;

    $state.cardsByFunnel = withoutCard($state.cardsByFunnel, card);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
